import type {MouseEvent} from 'react';
import {useCallback, useEffect} from 'react';

import type {SliderImage} from 'UI/components/modules/ImageSlider/ImageSlider';

/**
 * Custom hook for the `ImageZoomModal` component.
 * Manages body scroll lock and keyboard close behavior, and derives the
 * effective single-image / slider-mode display state from the provided images.
 *
 * @param isOpen   Whether the modal is currently open.
 * @param onClose  Callback to close the modal.
 * @param images   Optional array of slider images. When length > 1, slider mode is used.
 * @param imageUrl Optional single image URL, used as a fallback when `images` has 0 or 1 entries.
 * @returns An object containing:
 * - `handleBackdropClick`: Click handler for the backdrop overlay.
 * - `hasSlider`: Whether slider mode should be used.
 * - `singleImage`: The single image entry to display, or `null` when in slider mode.
 * - `singleUrl`: The effective single image URL, or `undefined` when in slider mode.
 */
export const useImageZoomModal = (
    isOpen: boolean,
    onClose: () => void,
    images?: SliderImage[],
    imageUrl?: string
) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;

        /**
         * Closes the modal on Escape key press.
         *
         * @param e The keyboard event.
         */
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    /**
     * Closes modal when clicking the backdrop (not the content).
     *
     * @param e The mouse event.
     */
    const handleBackdropClick = useCallback((e: MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    const hasSlider = Boolean(images && images.length > 1);
    const singleImage = !hasSlider && images?.length === 1 ? images[0] : null;
    const singleUrl = singleImage?.url ?? imageUrl;

    return {
        handleBackdropClick,
        hasSlider,
        singleImage,
        singleUrl
    };
};